import {
  addCalendarDate,
  CalendarDate,
  createCalendarDate,
  endOfCalendarMonth,
  endOfCalendarYear,
  formatCalendarDateDayMonth,
  formatCalendarMonthAndYear,
  getNumberOfDaysInInterval,
  getLocaleFromLanguage,
  getNumberOfMonthsBetweenCalendarDates,
  getQuarterNumber,
  getQuarterStartMonth,
  getSemesterNumber,
  getSemesterStartMonth,
  Interval,
  isAfter,
  isAfterOrEqual,
  isBefore,
  isBeforeOrEqual,
  isEqual,
  MONTHS_IN_QUARTER,
  MONTHS_IN_SEMESTER,
  MONTHS_IN_YEAR,
  setCalendarDate,
  startOfCalendarMonth,
  startOfCalendarYear,
  subtractCalendarDate,
} from './dateutils';
import { getNumberOfMonthsFromFrequency } from './leaseutils';
import { roundAtSecondDecimal } from './numbers';
import { capitalizeFirstLetter } from './strings';
import { InvoiceType, Language, LeasePaymentFrequency, LeasePaymentInvoicePeriod } from './types/model-app';

export const applyProRata = (amountToConvert: number, amountToCompare: number, totalAmountToCompare: number) => {
  if (totalAmountToCompare === 0) {
    return 0;
  }
  const ratio = amountToCompare / totalAmountToCompare;
  return roundAtSecondDecimal(amountToConvert * ratio);
};

export const applyProRataWithTimeInterval = (
  intervalToProRated: Interval,
  destinationInterval: Interval,
  amount: number
): number => {
  const overlapStart = isAfter(destinationInterval.start, intervalToProRated.start)
    ? destinationInterval.start
    : intervalToProRated.start;
  const overlapEnd = isBefore(destinationInterval.end, intervalToProRated.end)
    ? destinationInterval.end
    : intervalToProRated.end;
  if (isAfter(overlapStart, overlapEnd)) {
    return 0;
  }
  const overlappingDays = getNumberOfDaysInInterval({ start: overlapStart, end: overlapEnd });
  return applyProRata(amount, overlappingDays, getNumberOfDaysInInterval(intervalToProRated));
};

type PeriodLabelDateFormat = 'date' | 'monthAndYear';
export type DatePeriod = { periodFrom: CalendarDate; periodTo: CalendarDate; previousPeriodStart?: CalendarDate };

export const formatPeriodRange = (
  period: DatePeriod,
  language: Language,
  dateFormat: PeriodLabelDateFormat = 'date'
): string => {
  const formatDate = dateFormat === 'monthAndYear' ? formatCalendarMonthAndYear : formatCalendarDateDayMonth;
  const fromLabel = formatDate(period.periodFrom, language);
  if (isEqual(period.periodFrom, period.periodTo)) {
    return fromLabel;
  }

  return `${fromLabel} - ${formatDate(period.periodTo, language)}`;
};

const getExpectedPeriodForFrequency = (
  date: CalendarDate,
  paymentFrequency: LeasePaymentFrequency
): Interval | null => {
  switch (paymentFrequency) {
    case LeasePaymentFrequency.MONTHLY: {
      const start = startOfCalendarMonth(date);
      return {
        start,
        end: endOfCalendarMonth(start),
      };
    }
    case LeasePaymentFrequency.QUARTERLY: {
      const start = createCalendarDate(date.year, getQuarterStartMonth(date), 1);
      return {
        start,
        end: endOfCalendarMonth(addCalendarDate(start, MONTHS_IN_QUARTER - 1, 'months')),
      };
    }
    case LeasePaymentFrequency.BI_ANNUAL: {
      const start = createCalendarDate(date.year, getSemesterStartMonth(date), 1);
      return {
        start,
        end: endOfCalendarMonth(addCalendarDate(start, MONTHS_IN_SEMESTER - 1, 'months')),
      };
    }
    case LeasePaymentFrequency.YEARLY: {
      return {
        start: startOfCalendarYear(date),
        end: endOfCalendarYear(date),
      };
    }

    default:
      return null;
  }
};

const isFullPeriodForFrequency = (period: DatePeriod, paymentFrequency?: LeasePaymentFrequency | null): boolean => {
  if (!paymentFrequency) {
    return false;
  }

  const expectedPeriod = getExpectedPeriodForFrequency(period.periodFrom, paymentFrequency);

  if (!expectedPeriod) {
    return false;
  }

  return isEqual(period.periodFrom, expectedPeriod.start) && isEqual(period.periodTo, expectedPeriod.end);
};

const getYearLabel = (language: Language): string =>
  capitalizeFirstLetter(
    new Intl.DisplayNames(getLocaleFromLanguage(language).code, { type: 'dateTimeField' }).of('year') ?? ''
  );

const getLabelForPeriodStart = (
  startPeriod: CalendarDate,
  invoiceType: InvoiceType,
  paymentFrequency: LeasePaymentFrequency | null | undefined,
  language: Language
): string | null => {
  const startYear = startPeriod.year;
  const yearLabel = `${startYear}`;

  const monthYearLabel = capitalizeFirstLetter(formatCalendarMonthAndYear(startPeriod, language, 'long'));

  if (invoiceType !== InvoiceType.RENT) {
    return monthYearLabel;
  }

  switch (paymentFrequency) {
    case LeasePaymentFrequency.MONTHLY:
      return monthYearLabel;

    case LeasePaymentFrequency.YEARLY:
      return `${getYearLabel(language)} ${yearLabel}`;

    case LeasePaymentFrequency.QUARTERLY:
      return `Q${getQuarterNumber(startPeriod)} ${yearLabel}`;

    case LeasePaymentFrequency.BI_ANNUAL:
      return `S${getSemesterNumber(startPeriod)} ${yearLabel}`;

    default:
      return null;
  }
};

const normalizeExclusivePeriodEnd = (periodFrom: CalendarDate, periodTo: CalendarDate): CalendarDate =>
  periodTo.day === 1 && isAfter(periodTo, periodFrom) ? subtractCalendarDate(periodTo, 1, 'days') : periodTo;

const getDerivedLabelFromPeriod = (period: DatePeriod, language: Language): string | null => {
  const { periodFrom: start } = period;
  const end = normalizeExclusivePeriodEnd(period.periodFrom, period.periodTo);

  if (start.day !== 1 || !isEqual(end, endOfCalendarMonth(end)) || start.year !== end.year) {
    return null;
  }

  const year = start.year;
  const numberOfMonths = getNumberOfMonthsBetweenCalendarDates(start, end, true);

  if (start.month === 1 && numberOfMonths === MONTHS_IN_YEAR) {
    return `${getYearLabel(language)} ${year}`;
  }

  if (start.month === getSemesterStartMonth(start) && numberOfMonths === MONTHS_IN_SEMESTER) {
    return `S${getSemesterNumber(start)} ${year}`;
  }

  if (start.month === getQuarterStartMonth(start) && numberOfMonths === MONTHS_IN_QUARTER) {
    return `Q${getQuarterNumber(start)} ${year}`;
  }

  if (numberOfMonths === 1) {
    return capitalizeFirstLetter(formatCalendarMonthAndYear(start, language, 'long'));
  }

  return null;
};

export const getFormattedPeriodLabel = ({
  period,
  invoiceType = InvoiceType.RENT,
  paymentFrequency,
  language,
  dateFormat = 'date',
}: {
  period: DatePeriod;
  basePeriod?: DatePeriod;
  invoiceType?: InvoiceType;
  paymentFrequency?: LeasePaymentFrequency | null;
  language: Language;
  dateFormat?: PeriodLabelDateFormat;
}): string => {
  const labelFromFullPeriod =
    paymentFrequency &&
    isFullPeriodForFrequency(period, paymentFrequency) &&
    getLabelForPeriodStart(period.periodFrom, invoiceType, paymentFrequency, language);

  return (
    labelFromFullPeriod ||
    getDerivedLabelFromPeriod(period, language) ||
    formatPeriodRange(period, language, dateFormat)
  );
};

export const getStartMonthForInvoicePeriod = (
  startDate: CalendarDate,
  invoicePeriod: LeasePaymentInvoicePeriod,
  frequency: LeasePaymentFrequency
): number => {
  if (invoicePeriod !== LeasePaymentInvoicePeriod.CALENDAR_PERIOD) {
    return startDate.month;
  }

  switch (frequency) {
    case LeasePaymentFrequency.QUARTERLY:
      return getQuarterStartMonth(startDate);

    case LeasePaymentFrequency.BI_ANNUAL:
      return getSemesterStartMonth(startDate);

    case LeasePaymentFrequency.YEARLY:
      return 1;

    default:
      return startDate.month;
  }
};

const setDayOrStartOfNextMonth = (monthStart: CalendarDate, day: number): CalendarDate =>
  day <= monthStart.daysInMonth
    ? setCalendarDate(monthStart, day, 'day')
    : startOfCalendarMonth(addCalendarDate(monthStart, 1, 'months'));

export const getStartPeriodDay = (
  startDate: CalendarDate,
  invoicePeriod: LeasePaymentInvoicePeriod,
  customDay: number | undefined,
  frequency: LeasePaymentFrequency
): CalendarDate => {
  const startPeriodDay = getStartDayForInvoicePeriod(startDate, invoicePeriod, customDay, frequency);

  const startPeriodMonth = getStartMonthForInvoicePeriod(startDate, invoicePeriod, frequency);

  const candidateMonth = createCalendarDate(startDate.year, startPeriodMonth, 1);

  const candidate = setDayOrStartOfNextMonth(candidateMonth, startPeriodDay);

  if (!isBefore(startDate, candidate)) {
    return candidate;
  }

  const monthsOffset = getNumberOfMonthsFromFrequency(frequency);

  const previousPeriodMonth = startOfCalendarMonth(subtractCalendarDate(candidateMonth, monthsOffset, 'months'));

  return setDayOrStartOfNextMonth(previousPeriodMonth, startPeriodDay);
};

export const getStartDayForInvoicePeriod = (
  startDate: CalendarDate,
  invoicePeriod: LeasePaymentInvoicePeriod,
  customDay: number | undefined,
  frequency: LeasePaymentFrequency
): number => {
  if (frequency === LeasePaymentFrequency.BI_MONTHLY) {
    const lastDayOfFirstHalf = Math.floor(startDate.daysInMonth / 2);
    return startDate.day <= lastDayOfFirstHalf ? 1 : lastDayOfFirstHalf + 1;
  }

  if (invoicePeriod === LeasePaymentInvoicePeriod.CALENDAR_PERIOD) {
    return 1;
  }

  if (invoicePeriod === LeasePaymentInvoicePeriod.CUSTOM_PERIOD && customDay) {
    return customDay;
  }

  return startDate.day;
};

export const getNextBimestrialStart = (date: CalendarDate): CalendarDate => {
  const lastDayOfFirstHalf = Math.floor(date.daysInMonth / 2);

  if (date.day <= lastDayOfFirstHalf) {
    return setCalendarDate(date, lastDayOfFirstHalf + 1, 'day');
  }
  return startOfCalendarMonth(addCalendarDate(date, 1, 'months'));
};

export const getPrevBimestrialStart = (date: CalendarDate): CalendarDate => {
  const firstDayOfSecondHalf = Math.floor(date.daysInMonth / 2) + 1;

  if (date.day === firstDayOfSecondHalf) {
    return startOfCalendarMonth(date);
  }

  const previousMonth = subtractCalendarDate(date, 1, 'months');
  const firstDayOfSecondHalfOfPreviousMonth = Math.floor(previousMonth.daysInMonth / 2) + 1;

  return setCalendarDate(previousMonth, firstDayOfSecondHalfOfPreviousMonth, 'day');
};

export const getPreviousPeriodStart = (
  periodFrom: CalendarDate,
  frequency: LeasePaymentFrequency | keyof typeof LeasePaymentFrequency
): CalendarDate =>
  frequency === LeasePaymentFrequency.BI_MONTHLY
    ? getPrevBimestrialStart(periodFrom)
    : subtractCalendarDate(periodFrom, getNumberOfMonthsFromFrequency(frequency), 'months');

export const getNextPeriodStartDate = (
  startDate: CalendarDate,
  invoicePeriod: LeasePaymentInvoicePeriod,
  customDay: number | undefined,
  frequency: LeasePaymentFrequency
): CalendarDate => {
  const startDayOfMonth = getStartDayForInvoicePeriod(startDate, invoicePeriod, customDay, frequency);
  const startPeriodMonth = getStartMonthForInvoicePeriod(startDate, invoicePeriod, frequency);
  const candidateMonth = createCalendarDate(startDate.year, startPeriodMonth, 1);
  const candidate = setCalendarDate(candidateMonth, Math.min(startDayOfMonth, candidateMonth.daysInMonth), 'day');

  if (isBefore(startDate, candidate)) {
    return candidate;
  }

  return getNextStartDate(candidate, startDayOfMonth, frequency);
};

export const getNextStartDate = (
  previousStartDate: CalendarDate,
  startDayOfMonth: number,
  frequency: LeasePaymentFrequency
): CalendarDate => {
  if (frequency === LeasePaymentFrequency.BI_MONTHLY) {
    return getNextBimestrialStart(previousStartDate);
  }

  const months = getNumberOfMonthsFromFrequency(frequency);
  const didOverflow = previousStartDate.day !== startDayOfMonth;
  const isClampedToLastDay =
    didOverflow &&
    previousStartDate.day === previousStartDate.daysInMonth &&
    startDayOfMonth > previousStartDate.daysInMonth;

  if (isClampedToLastDay) {
    return startOfCalendarMonth(addCalendarDate(previousStartDate, 1, 'months'));
  }

  const referenceDate = didOverflow ? subtractCalendarDate(previousStartDate, 1, 'months') : previousStartDate;
  const nextMonth = addCalendarDate(referenceDate, months, 'months');

  if (startDayOfMonth > nextMonth.daysInMonth) {
    return startOfCalendarMonth(addCalendarDate(nextMonth, 1, 'months'));
  }
  return setCalendarDate(nextMonth, startDayOfMonth, 'day');
};

export const computeInvoicingPeriods = (
  startDate: CalendarDate,
  endDate: CalendarDate,
  invoicePeriod: LeasePaymentInvoicePeriod,
  frequency: LeasePaymentFrequency,
  customDay?: number,
  lastInvoiceDate?: CalendarDate,
  numberOfPeriodsToKeep: number = 0,
  skipPeriodsEndedBefore: CalendarDate | undefined = undefined
): DatePeriod[] => {
  const periods: DatePeriod[] = [];

  const startPeriodDay = getStartPeriodDay(startDate, invoicePeriod, customDay, frequency);

  let currentStart = startPeriodDay;
  let previousStart: CalendarDate | undefined;

  if (lastInvoiceDate && isAfterOrEqual(lastInvoiceDate, startPeriodDay)) {
    const shouldNormalizeClippedFirstInvoiceDate =
      isEqual(lastInvoiceDate, startDate) && !isEqual(lastInvoiceDate, startPeriodDay);
    const effectiveLastInvoiceDate = shouldNormalizeClippedFirstInvoiceDate ? startPeriodDay : lastInvoiceDate;
    const startDayOfMonth = getStartDayForInvoicePeriod(effectiveLastInvoiceDate, invoicePeriod, customDay, frequency);

    currentStart = getNextStartDate(effectiveLastInvoiceDate, startDayOfMonth, frequency);
    previousStart = effectiveLastInvoiceDate;
  }

  while (isBeforeOrEqual(currentStart, endDate)) {
    const startDayOfMonth = getStartDayForInvoicePeriod(currentStart, invoicePeriod, customDay, frequency);
    const nextStart = getNextStartDate(currentStart, startDayOfMonth, frequency);
    const periodFrom = isBefore(currentStart, startDate) ? startDate : currentStart;
    const periodEndBeforeClip = subtractCalendarDate(nextStart, 1, 'days');
    const periodTo = isAfter(periodEndBeforeClip, endDate) ? endDate : periodEndBeforeClip;

    const shouldKeepPeriod =
      isBeforeOrEqual(periodFrom, periodTo) &&
      (!skipPeriodsEndedBefore || isAfterOrEqual(periodTo, skipPeriodsEndedBefore));

    if (shouldKeepPeriod) {
      periods.push({
        periodFrom,
        periodTo,
        previousPeriodStart: previousStart,
      });

      if (numberOfPeriodsToKeep > 0 && periods.length === numberOfPeriodsToKeep) {
        return periods;
      }
    }

    previousStart = currentStart;
    currentStart = nextStart;
  }

  return periods;
};
